import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import api from '../api/axios';
import Loading from '../components/Loading';
import { Restaurant, UpdateRestaurantDto, Address } from '../types';
import './Pages.css';

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState<UpdateRestaurantDto>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const response = await api.get<Restaurant>('/admin/profile');
      setProfile(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    
    setSubmitting(true);
    try {
      await api.put('/admin/profile', formData);
      setEditing(false);
      loadProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Erro ao atualizar perfil');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      address: {
        ...(prev.address || {} as Address),
        [name]: value
      }
    }));
  };

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('Tipo de arquivo não suportado. Use JPG ou PNG.');
      return;
    }

    // Validar tamanho (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('Arquivo muito grande. Tamanho máximo: 5MB.');
      return;
    }

    setLogoFile(file);

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !profile) return;

    setUploadingLogo(true);
    try {
      const formDataLogo = new FormData();
      formDataLogo.append('file', logoFile);
      
      await api.post(`/admin/restaurants/${profile.restaurantId}/logo`, formDataLogo, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Recarregar perfil para obter a nova URL da logo
      await loadProfile();
      setLogoFile(null);
      setLogoPreview(null);
      alert('Logo atualizada com sucesso!');
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      alert(error.response?.data?.message || 'Erro ao fazer upload da logo. Tente novamente.');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) return <Loading message="Carregando perfil..." />;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Perfil do Restaurante</h1>
          <p>Gerencie as informações do seu estabelecimento</p>
        </div>
        {!editing && (
          <button className="btn-primary" onClick={() => setEditing(true)}>
            Editar Perfil
          </button>
        )}
      </div>

      {editing ? (
        <div className="profile-form">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nome do Restaurante</label>
              <input
                type="text"
                name="name"
                value={formData.name || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Descrição</label>
              <textarea
                name="description"
                value={formData.description || ''}
                onChange={handleInputChange}
                rows={4}
              />
            </div>
            <div className="form-group">
              <label>E-mail</label>
              <input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Logo do Restaurante</label>
              {profile?.logoUrl && !logoPreview && (
                <div style={{ marginBottom: '10px' }}>
                  <img 
                    src={profile.logoUrl} 
                    alt="Logo atual" 
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '200px', 
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '4px'
                    }} 
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleLogoChange}
                disabled={uploadingLogo}
              />
              {logoPreview && (
                <div style={{ marginTop: '10px' }}>
                  <img 
                    src={logoPreview} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '200px', 
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '4px'
                    }} 
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleLogoUpload}
                    disabled={uploadingLogo}
                    style={{ marginTop: '10px', display: 'block' }}
                  >
                    {uploadingLogo ? 'Enviando...' : 'Enviar Logo'}
                  </button>
                </div>
              )}
              <small className="form-hint">
                📷 Tamanho recomendado: 400x400 pixels. Formatos aceitos: JPG, PNG (máx. 5MB).
                <br />
                ⚠️ A imagem será redimensionada automaticamente para 400x400 pixels mantendo as proporções.
              </small>
            </div>
            <div className="form-group">
              <label>Endereço</label>
              <input
                type="text"
                name="street"
                placeholder="Rua"
                value={formData.address?.street || ''}
                onChange={handleAddressChange}
              />
              <div className="form-row">
                <input
                  type="text"
                  name="number"
                  placeholder="Número"
                  value={formData.address?.number || ''}
                  onChange={handleAddressChange}
                />
                <input
                  type="text"
                  name="neighborhood"
                  placeholder="Bairro"
                  value={formData.address?.neighborhood || ''}
                  onChange={handleAddressChange}
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  name="city"
                  placeholder="Cidade"
                  value={formData.address?.city || ''}
                  onChange={handleAddressChange}
                />
                <input
                  type="text"
                  name="state"
                  placeholder="Estado"
                  value={formData.address?.state || ''}
                  onChange={handleAddressChange}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setEditing(false)} disabled={submitting}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="profile-view">
          {profile?.logoUrl && (
            <div className="profile-section" style={{ textAlign: 'center', marginBottom: '20px' }}>
              <img 
                src={profile.logoUrl} 
                alt={profile.name} 
                style={{ 
                  maxWidth: '200px', 
                  maxHeight: '200px', 
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '8px'
                }} 
              />
            </div>
          )}
          <div className="profile-section">
            <h3>Informações Básicas</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Nome:</span>
                <span>{profile?.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Telefone:</span>
                <span>{profile?.phone}</span>
              </div>
              <div className="info-item">
                <span className="info-label">E-mail:</span>
                <span>{profile?.email || 'Não informado'}</span>
              </div>
            </div>
            {profile?.description && (
              <div className="info-item">
                <span className="info-label">Descrição:</span>
                <p>{profile.description}</p>
              </div>
            )}
          </div>
          
          <div className="profile-section">
            <h3>Endereço</h3>
            <p>
              {profile?.address?.street}, {profile?.address?.number}<br />
              {profile?.address?.neighborhood}<br />
              {profile?.address?.city} - {profile?.address?.state}<br />
              CEP: {profile?.address?.zipCode}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;


